import React, {
  createContext,
  Fragment,
  PropsWithChildren,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import type {
  FlatList,
  StyleProp,
  TransformsStyle,
  ViewStyle,
} from "react-native";
import type { AnimatedStyle, SharedValue } from "react-native-reanimated";
import Animated, {
  interpolateColor,
  makeMutable,
  useAnimatedScrollHandler,
  useAnimatedStyle,
  useSharedValue,
} from "react-native-reanimated";
import { View, ViewProps, XStack, XStackProps } from "tamagui";

interface ISwiperContext {
  data: ArrayLike<unknown>;
  containerWidth: number;
  setContainerWidth: React.Dispatch<React.SetStateAction<number>>;
  scrollX: SharedValue<number>;
}

const SwiperContext = createContext<ISwiperContext>({
  data: [],
  containerWidth: 0,
  setContainerWidth: () => console.warn("SwiperContext not provided"),
  scrollX: makeMutable(0),
});

const useSwiper = () => {
  const context = useContext(SwiperContext);
  if (!context) {
    throw new Error("useSwiper must be used within a SwiperProvider");
  }
  return context;
};

const SwiperProvider: React.FC<
  PropsWithChildren & {
    data: ArrayLike<unknown>;
  }
> = ({ data, children }) => {
  const [containerWidth, setContainerWidth] = useState(0);

  const scrollX = useSharedValue(0);
  return (
    <SwiperContext.Provider
      value={{
        data,
        containerWidth,
        setContainerWidth,
        scrollX,
      }}
    >
      {children}
    </SwiperContext.Provider>
  );
};

type SwiperContainerProps = ViewProps & {
  children: (props: { item: any; index: number }) => React.ReactElement;
  contentContainerStyle?: StyleProp<AnimatedStyle<StyleProp<ViewStyle>>>;
  bullets?: React.ReactNode;
  initialIndex?: number;
};

const SwiperContainer: React.FC<SwiperContainerProps> = ({
  children,
  contentContainerStyle,
  bullets,
  initialIndex,
  ...rest
}) => {
  const { data, containerWidth, setContainerWidth, scrollX } = useSwiper();
  const flatListRef = useRef<FlatList>(null);
  const isScrolled = useRef<boolean>(false);

  const scrollHandler = useAnimatedScrollHandler((event) => {
    scrollX.value = event.contentOffset.x;
  });

  useEffect(() => {
    if (
      !isScrolled.current &&
      containerWidth > 0 &&
      initialIndex !== undefined
    ) {
      try {
        flatListRef.current?.scrollToIndex({ index: initialIndex });
        isScrolled.current = true;
      } catch (e) {
        console.error("Initial scroll error", e);
      }
    }
  }, [containerWidth, initialIndex]);

  return (
    <View
      width="100%"
      onLayout={(event) => {
        setContainerWidth(event.nativeEvent.layout.width);
      }}
      {...rest}
    >
      {containerWidth > 0 && (
        <Animated.FlatList
          ref={flatListRef}
          data={data}
          keyExtractor={(_, i) => i.toString()}
          horizontal
          pagingEnabled
          showsHorizontalScrollIndicator={false}
          renderItem={children}
          getItemLayout={(_, index) => ({
            length: containerWidth,
            offset: containerWidth * index,
            index,
          })}
          onScroll={scrollHandler}
          contentContainerStyle={contentContainerStyle}
          scrollEventThrottle={16}
        />
      )}
    </View>
  );
};

const SwiperItem: React.FC<Omit<ViewProps, "width">> = ({
  children,
  ...rest
}) => {
  const { containerWidth } = useSwiper();
  return (
    <View flex={1} width={containerWidth} {...rest}>
      {children}
    </View>
  );
};

const SwiperBulletsContainer: React.FC<
  Omit<XStackProps, "children"> & {
    children: (index: number) => React.ReactNode;
  }
> = ({ children, ...rest }) => {
  const { data } = useSwiper();

  return (
    <XStack {...rest}>
      {Array.isArray(data)
        ? data.map((_, index) => (
            <Fragment key={index}>{children(index)}</Fragment>
          ))
        : null}
    </XStack>
  );
};

interface BulletProps {
  index: number;
  style?: StyleProp<AnimatedStyle<StyleProp<ViewStyle>>>;
  bulletColors?: {
    active: string;
    inactive: string;
  };
  transform?: (active: boolean) => TransformsStyle["transform"];
}

const SwiperBullet: React.FC<BulletProps> = ({
  index,
  style,
  bulletColors,
  transform,
}) => {
  const { containerWidth: width, scrollX } = useSwiper();

  const animatedStyle = useAnimatedStyle(() => {
    const active = Math.round(scrollX.value / width) === index;

    const inputRange = [
      (index - 1) * width,
      index * width,
      (index + 1) * width,
    ];

    const backgroundColorArr = bulletColors
      ? [bulletColors?.inactive, bulletColors?.active, bulletColors?.inactive]
      : ["#CDD3DC", "#02302A", "#CDD3DC"];

    const backgroundColor = interpolateColor(
      scrollX.value,
      inputRange,
      backgroundColorArr,
    );

    return {
      backgroundColor,
      transform: transform?.(active),
    };
  });

  return <Animated.View key={index} style={[style, animatedStyle]} />;
};

export const Swiper = {
  Provider: SwiperProvider,
  Container: SwiperContainer,
  Item: SwiperItem,
  Bullet: SwiperBullet,
  BulletsContainer: SwiperBulletsContainer,
};
